import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  ExpenseReportByView,
  ExpenseReportStatus,
  ExpenseStatus,
  ReportByViewExpense,
  UserRole,
} from "@common/utils/graphql";
import { useMemo } from "react";
import { Separator } from "@/components/ui/separator";
import { reportsTableColumns } from "../ReportsTable";
import { cn } from "@/utils/utils";
import { ConfirmationDialog } from "@common/components/ConfirmationDialog/ConfirmationDialog";
import { DataTable } from "@common/components/Table/DataTable";
import { ReportsTrigger } from "../admin/AdminReports";
import { useRowActions } from "./useRowAction";
import { useReportActions } from "./useReportActions";
import { formatDate } from "@common/components/utils";
import { Check, CircleNotch, Info, X } from "@phosphor-icons/react";
import { useOutOfPolicyExpense } from "@common/state/outOfPolicyState";
import { roleState } from "@common/state/roleState";
import { useRecoilValue } from "recoil";
import { Body } from "@hypercard/web-components";
import { ApproveOrRejectReportDialog } from "./ApproveOrRejectReportDialog";
import { Row } from "@tanstack/react-table";
import { StatusText } from "@common/components/StatusText";

interface ReportsCardProps {
  report: ExpenseReportByView;
  onClick: (trigger: ReportsTrigger) => void;
  mode?: "edit" | "review";
  onSubmitted?: () => void;
}

export const ReportsCard = ({
  report,
  onClick,
  mode,
  onSubmitted,
}: ReportsCardProps) => {
  const {
    createdAt,
    displayTotalDollarAmount,
    displayPayoutMethod,
    title,
    notes,
    expenses,
    status,
    user,
  } = report;

  const role = useRecoilValue(roleState);
  const transactionCount = expenses?.length || 0;
  const isReviewMode = mode === "review";

  const { isExpenseOutOfPolicy } = useOutOfPolicyExpense();
  const { approvedIds, rejectedIds, applyRows, applyAllRows } =
    useRowActions(expenses);
  const {
    onSubmitReport,
    isReportOutOfPolicy,
    isHidden,
    onSubmitReviewedReport,
    loading,
  } = useReportActions(report, approvedIds, onSubmitted);

  const expenseData = useMemo(() => {
    return expenses.map((expense) => ({
      ...expense,
      status: approvedIds.find((e) => e === expense.id)
        ? ExpenseStatus.Approved
        : rejectedIds.find((e) => e === expense.id)
          ? ExpenseStatus.Rejected
          : undefined || expense.status,
    })) as ReportByViewExpense[];
  }, [approvedIds, rejectedIds, expenses]);

  const columns = reportsTableColumns(
    applyRows,
    [
      ExpenseReportStatus.Approved,
      ExpenseReportStatus.Rejected,
      ExpenseReportStatus.PartiallyApproved,
    ].includes(report.status),
    isReviewMode && report.status === ExpenseReportStatus.Pending,
    isExpenseOutOfPolicy,
  );

  const isCompleted =
    approvedIds.length + rejectedIds.length === expenses.length;
  const allApproved = approvedIds.length === expenses.length;
  const allRejected = rejectedIds.length === expenses.length;
  const isApprovedOrPartiallyApproved =
    status === ExpenseReportStatus.Approved ||
    status === ExpenseReportStatus.PartiallyApproved;
  const hasDesignatedReviewer =
    status === ExpenseReportStatus.Pending && role === UserRole.Employee;

  const onOpenDetails = () => {
    onClick({ report, isEditModeDefault: false });
  };

  if (isHidden) {
    return null;
  }

  const header = (
    <div className="flex flex-row justify-between">
      <CardHeader className="flex items-start justify-start">
        <div className="mb-3 flex h-5 items-center space-x-4 text-sm">
          <div className="text-slate">{formatDate(createdAt)}</div>
          <Separator orientation="vertical" />
          <StatusText {...report} size="sm" />
        </div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{notes || "No notes"}</CardDescription>
      </CardHeader>
      <div className="flex items-center justify-center gap-5">
        <Button variant="secondary" onClick={onOpenDetails}>
          Additional Report Details
          <Info className="ml-2 size-4" />{" "}
        </Button>
        <AccordionTrigger className="pr-5" />
      </div>
    </div>
  );

  const body = (
    <DataTable
      columns={columns}
      isErrorRow={isExpenseOutOfPolicy}
      data={expenseData}
      onClick={(row: Row<ReportByViewExpense>) =>
        onClick({ report, isEditModeDefault: false, expense: row.original })
      }
    />
  );

  const footerInfo = (
    <div className="flex gap-10">
      {role !== UserRole.Employee && (
        <div>
          <div className="text-sm text-slate">Employee</div>
          <div className="text-xl">
            {user.firstName} {user.lastName}
          </div>
        </div>
      )}
      <div>
        <div className="text-sm text-slate">Expenses</div>
        <div className="text-xl">
          {transactionCount} Expense{transactionCount !== 1 ? "s" : ""}
        </div>
      </div>
      <div>
        <div className="text-sm text-slate">Total Amount</div>
        <div className="text-xl">{displayTotalDollarAmount}</div>
      </div>
      {isApprovedOrPartiallyApproved ? (
        <div>
          <div className="text-sm text-slate">Reimbursement Method</div>
          <div className="text-xl">{displayPayoutMethod || "Pending"}</div>
        </div>
      ) : null}
      {report.approvedOrRejectedBy?.displayName && (
        <div>
          <div className="text-sm text-slate">Reviewed By</div>
          <div className="text-xl">
            {report.approvedOrRejectedBy?.displayName}
          </div>
        </div>
      )}
      {hasDesignatedReviewer && (
        <div>
          <div className="text-sm text-slate">Designated Reviewer</div>
          <div className="text-xl">
            {report.designatedApprover?.displayName ?? "Any Admin"}
          </div>
        </div>
      )}
    </div>
  );

  const footer = (
    <>
      {status === ExpenseReportStatus.Draft && (
        <div className="flex gap-4">
          <SheetTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onClick({ report, isEditModeDefault: true })}
            >
              Edit Report
            </Button>
          </SheetTrigger>
          {isReportOutOfPolicy ? (
            <Button
              size="sm"
              disabled={loading || report.expenses.length === 0}
              onClick={onSubmitReport}
            >
              {loading ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                "Submit Anyways"
              )}
            </Button>
          ) : (
            <ConfirmationDialog
              title="Submit report?"
              disabled={loading || report.expenses.length === 0}
              description={
                "Are you sure you want to submit this report?\nThis action is irreversible"
              }
              onConfirm={onSubmitReport}
              asChild
            >
              <Button
                size="sm"
                disabled={loading || report.expenses.length === 0}
              >
                {loading ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : (
                  "Submit Report"
                )}
              </Button>
            </ConfirmationDialog>
          )}
        </div>
      )}
      {isReviewMode && status === ExpenseReportStatus.Pending && (
        <div className="flex gap-4">
          <Button
            variant={allApproved ? "success" : "secondary"}
            size="sm"
            disabled={loading}
            onClick={() => applyAllRows(true)}
            className="w-[120px]"
          >
            {allApproved ? "Approved all" : "Approve All"}
            <Check
              className={cn("ml-2 h-4 w-4", !allApproved && "text-green-600")}
            />
          </Button>
          <Button
            variant={allRejected ? "danger" : "secondary"}
            size="sm"
            disabled={loading}
            onClick={() => applyAllRows(false)}
            className="w-[120px]"
          >
            {allRejected ? "Rejected all" : "Reject All"}
            <X
              className={cn("ml-2 h-4 w-4", !allRejected && "text-rose-600")}
            />
          </Button>
          <ApproveOrRejectReportDialog
            onConfirm={onSubmitReviewedReport}
            isLoading={loading}
            isApprovingAll={allApproved}
            isRejectingAll={allRejected}
            employeeName={user.firstName}
            renderTrigger={() => (
              <Button
                variant="default"
                size="sm"
                disabled={!isCompleted || loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            )}
          />
        </div>
      )}
    </>
  );

  const employerNotesSubFooter = report.employerNotes && (
    <div className="flex bg-[#1b1b1b] px-6 py-4">
      <Body size="sm" weight="regular" className="mr-3 text-white/50">
        {report.isAutoApproved ? "Auto-Approval Reason:" : "Employer Notes:"}
      </Body>
      <Body size="sm" weight="regular">{`"${report.employerNotes}"`}</Body>
    </div>
  );

  return (
    <Card className="mt-5 overflow-hidden">
      <Accordion
        key={`expense-report-card--expense-report-${report.id}-out-of-policy-${isReportOutOfPolicy}`}
        type="single"
        defaultValue={
          expenseData.length <= 8 || isReportOutOfPolicy ? "item-1" : undefined
        }
        collapsible
      >
        <AccordionItem value="item-1" className="border-0">
          {header}
          <AccordionContent>
            <CardContent className="mx-6 justify-between border-t bg-transparent p-0 pb-0 pt-4">
              {body}
            </CardContent>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <CardFooter className="mx-6 flex justify-between border-t bg-transparent p-0 pb-6 pt-4">
        {footerInfo}
        {footer}
      </CardFooter>
      {employerNotesSubFooter}
    </Card>
  );
};
